import { Vector3 } from 'three';
import { TrackRuntime } from '../track/runtime';
import { CapsuleBVH, Capsule, segmentSegmentDistance as segDist } from './bvh';
import {
  SupportResult,
  TrackSupportPrimitive,
  PlacementStrategy,
  SupportOverride,
  mergeFeetMembers,
} from './types';
import { trackEnvelopeRadius, ARROW_PROFILE } from '../geometry/profile';

/** Terrain interface — flat plane in v1, heightmap stays possible later. */
export interface Terrain {
  heightAt(x: number, z: number): number;
}

export const FLAT_TERRAIN: Terrain = { heightAt: () => 0 };

export interface SupportParams {
  /** Number of primary supports distributed evenly along track arc length. */
  supportCount: number;
  trackSpacing: number; // max arc-length gap before a hanger to an existing support
  maxSpan: number; // max unsupported XZ distance between ground supports
  columnRadius: number;
  braceRadius: number;
  maxLeanDeg: number;
  clearance: number; // extra radius around the track envelope (train + margin)
  minTrackHeight: number; // below this the track needs no column
  braceMinHeight: number; // braces only at/above this height above the higher foot
  /** @deprecated Unused — braces are straight (level) beams; kept for saved docs. */
  braceAngleDeg: number;
  beamHeightGridOffset: number; // world-Y of the first allowed brace connection
  beamHeightGridStep: number; // world-Y spacing between allowed brace connections
  attachDepth: number; // rail line → structural attach point along −up
  attachStub: number; // short stub along −up from attach before bending into the column
  attachStubRadius: number; // stub radius at the track (tapers to columnRadius at the elbow)
  /** Ground spread for a 2nd main leg (triangle). 0 = single leg only. */
  triangleSpread: number;
  /** Only try a 2nd leg when the primary leans at least this far from vertical. */
  triangleMinLeanDeg: number;
  /** At/above this column height, always add a 2nd main leg (even if vertical). */
  triangleMinHeight: number;
  /** When true, braces only attach to main legs — not triangle mate legs. */
  braceMainOnly: boolean;
  /** Merge ground footers whose plan distance is at or below this (0 = off). */
  footerMergeDistance: number;
  /**
   * When true, main and triangle mate footers may merge onto a shared pad.
   * When false, a triangle mate footer never merges with a main-column footer.
   */
  allowTriangleMerge: boolean;
}

export const DEFAULT_SUPPORT_PARAMS: SupportParams = {
  supportCount: 50,
  trackSpacing: 8,
  maxSpan: 12,
  columnRadius: 0.22,
  braceRadius: 0.1,
  maxLeanDeg: 25,
  clearance: 1.0,
  minTrackHeight: 1.2,
  braceMinHeight: 8,
  braceAngleDeg: 45,
  beamHeightGridOffset: 1,
  beamHeightGridStep: 10,
  attachDepth: 0.95,
  attachStub: 0.5,
  attachStubRadius: 0.35,
  triangleSpread: 2.5,
  triangleMinLeanDeg: 5,
  triangleMinHeight: 6,
  braceMainOnly: false,
  footerMergeDistance: 1.0,
  allowTriangleMerge: true,
};

/** Horizontal (plan) distance — XZ only, height ignored. */
export function horizDistXZ(a: Vector3, b: Vector3): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.hypot(dx, dz);
}

/** World-Y heights on the brace grid that fall inside [yLo, yHi]. */
export function braceGridHeights(
  yLo: number,
  yHi: number,
  offset: number,
  step: number,
): number[] {
  const s = Math.max(0.1, step);
  const out: number[] = [];
  let k = Math.ceil((yLo - offset) / s - 1e-9);
  let y = offset + k * s;
  // Guard against floating drift below yLo
  while (y < yLo - 1e-6) {
    k += 1;
    y = offset + k * s;
  }
  while (y <= yHi + 1e-6) {
    out.push(y);
    k += 1;
    y = offset + k * s;
  }
  return out;
}

interface PlacedColumn {
  attachS: number;
  top: Vector3;
  foot: Vector3;
  /** Main ground leg vs the optional triangle mate leg. */
  role: 'main' | 'triangle';
}

/** True if arc length s falls inside a suppress range (inclusive). */
export function isSuppressed(s: number, overrides: SupportOverride[]): boolean {
  for (const o of overrides) {
    if (o.kind !== 'suppress') continue;
    const lo = Math.min(o.from, o.to);
    const hi = Math.max(o.from, o.to);
    if (s >= lo && s <= hi) return true;
  }
  return false;
}

/** Arc spacing between evenly placed primary supports: L / count. */
export function supportArcSpacing(totalLength: number, supportCount: number): number {
  const n = Math.max(1, Math.floor(supportCount));
  if (totalLength < 1e-6) return 1;
  return totalLength / n;
}

/** Suppress window around one attach station (half the primary spacing). */
export function suppressRangeForAttach(
  attachS: number,
  totalLength: number,
  supportCount: number,
): { from: number; to: number } {
  const half = supportArcSpacing(totalLength, supportCount) * 0.5;
  return { from: Math.max(0, attachS - half), to: attachS + half };
}

/**
 * Arc-length neighbourhood around an attach where a column may intersect the
 * track BVH. Must be at least the sum of track+clearance and column radii —
 * otherwise thick columns / large clearance reject against neighbouring
 * capsules of the same span (common on tall climbs).
 */
export function attachExcludeRange(params: SupportParams, arcSpacing: number): number {
  const trackR = trackEnvelopeRadius(ARROW_PROFILE) + params.clearance;
  const byRadii = trackR + params.columnRadius + 0.5;
  return Math.max(Math.max(0.5, arcSpacing) * 0.7, byRadii);
}

/** Build the collision index: one capsule per centerline step, radius = track envelope + train clearance. */
export function buildTrackBVH(runtime: TrackRuntime, clearance: number): CapsuleBVH {
  const caps: Capsule[] = [];
  const step = 2;
  const L = runtime.totalLength;
  const r = trackEnvelopeRadius(ARROW_PROFILE) + clearance;
  let prev = runtime.frameAt(0).railPos.clone();
  for (let s = step; s <= L + 0.001; s += step) {
    const f = runtime.frameAt(Math.min(s, L));
    caps.push({ a: prev, b: f.railPos.clone(), r, s: s - step / 2 });
    prev = f.railPos.clone();
  }
  return new CapsuleBVH(caps);
}

/**
 * Candidate attach arc lengths: `supportCount` stations evenly along the track,
 * starting at start + 0.5 × (L / count).
 */
export function sampleBySupportCount(runtime: TrackRuntime, supportCount: number): number[] {
  const L = runtime.totalLength;
  if (L < 1e-6) return [];
  const n = Math.max(1, Math.floor(supportCount));
  const spacing = L / n;
  const candidates: number[] = [];
  for (let i = 0; i < n; i++) {
    candidates.push((i + 0.5) * spacing);
  }
  return candidates;
}

/**
 * The support solver: sample → anchor → cast → reject (no-clip rule) →
 * recover (lean, A-frame, knee, skip) → enforce max span → force overrides → brace.
 * Purely declarative output; no meshes in core.
 */
export function generateSupports(
  runtime: TrackRuntime,
  terrain: Terrain = FLAT_TERRAIN,
  params: SupportParams = DEFAULT_SUPPORT_PARAMS,
  overrides: SupportOverride[] = [],
): SupportResult {
  const prims: TrackSupportPrimitive[] = [];
  const warnings: string[] = [];
  const skipped: number[] = [];
  const placed: PlacedColumn[] = [];

  const L = runtime.totalLength;
  if (L < 1) return { primitives: prims, feet: [], skipped, warnings };

  const bvh = buildTrackBVH(runtime, params.clearance);
  const arcSpacing = supportArcSpacing(L, params.supportCount);

  // 1. SAMPLE — `supportCount` stations evenly along arc length
  //    (first at start + 0.5 × L/count).
  const candidates = sampleBySupportCount(runtime, params.supportCount);
  const supportedS: number[] = [];

  // 2–5. place each candidate (skip suppressed ranges)
  for (const cs of candidates) {
    if (isSuppressed(cs, overrides)) {
      skipped.push(cs);
      continue;
    }
    const ok = placeAt(cs, false);
    if (ok) supportedS.push(cs);
    else skipped.push(cs);
  }

  // 6. SPAN — force a solution inside any XZ gap wider than maxSpan (outside suppress)
  const placedOrdered = [...placed].sort((a, b) => a.attachS - b.attachS);
  if (placedOrdered.length === 0) {
    fillHorizGap(0, L);
  } else {
    fillHorizGap(0, placedOrdered[0].attachS);
    for (let i = 0; i < placedOrdered.length; i++) {
      const a = placedOrdered[i];
      if (i + 1 < placedOrdered.length) {
        fillHorizGap(a.attachS, placedOrdered[i + 1].attachS);
      } else if (runtime.closed) {
        fillHorizGap(a.attachS, placedOrdered[0].attachS + L);
      } else {
        fillHorizGap(a.attachS, L);
      }
    }
  }

  function fillHorizGap(s0: number, s1: number): void {
    if (s1 - s0 < 1e-3) return;
    const walkStep = 0.5;
    let prev = runtime.frameAt(runtime.centerline.normalizeS(s0)).pos.clone();
    let accum = 0;
    for (let s = s0 + walkStep; s < s1 - 1e-6; s += walkStep) {
      const sn = runtime.centerline.normalizeS(s);
      const pos = runtime.frameAt(sn).pos;
      accum += horizDistXZ(prev, pos);
      prev.copy(pos);
      if (accum < params.maxSpan) continue;
      accum = 0;
      if (isSuppressed(sn, overrides)) continue;
      if (placeAt(sn, true)) supportedS.push(sn);
      else warnings.push(`No valid support found near s=${sn.toFixed(0)} m — plan span exceeds ${params.maxSpan} m`);
    }
  }

  // 6b. FORCE overrides — always try, even inside suppress ranges.
  // forceFoot also implies a support must exist at that attach so the foot can move.
  for (const o of overrides) {
    if (o.kind !== 'force' && o.kind !== 'forceFoot') continue;
    const fs = runtime.centerline.normalizeS(o.s);
    if (placeAt(fs, true)) supportedS.push(fs);
    else warnings.push(`Forced support at s=${fs.toFixed(0)} m could not be placed`);
  }

  // 6c. TRACK SPACING — along-track gaps: hangers to existing supports, or a
  // ground column when that beam would be shorter than the hanger.
  fillTrackHangers();

  // 6c2. TRIANGLE RETARGET — when hangers planted a joint on the main mast,
  // connect the triangle mate to that sphere instead of the track elbow.
  retargetTriangleTopsToHangerJoints();

  function fillTrackHangers(): void {
    const spacing = Math.max(0.5, params.trackSpacing);
    if (placed.length === 0) return;
    const marks = [...supportedS].sort((a, b) => a - b);
    if (marks.length === 0) return;

    const gaps: [number, number][] = [];
    if (marks[0] > spacing) gaps.push([0, marks[0]]);
    for (let i = 0; i < marks.length; i++) {
      const a = marks[i];
      if (i + 1 < marks.length) {
        const b = marks[i + 1];
        if (b - a > spacing) gaps.push([a, b]);
      } else if (runtime.closed) {
        const b = marks[0] + L;
        if (b - a > spacing) gaps.push([a, b]);
      } else if (L - a > spacing) {
        gaps.push([a, L]);
      }
    }

    for (const [a, b] of gaps) {
      const n = Math.floor((b - a) / spacing);
      for (let i = 1; i <= n; i++) {
        const gs = runtime.centerline.normalizeS(a + ((b - a) * i) / (n + 1));
        if (isSuppressed(gs, overrides)) continue;
        if (nearestSupportedDist(gs, marks) < spacing * 0.35) continue;
        if (placeTrackSpacing(gs)) {
          marks.push(gs);
          marks.sort((x, y) => x - y);
          supportedS.push(gs);
        } else {
          warnings.push(`No track-spacing support found near s=${gs.toFixed(0)} m — track spacing ${spacing} m`);
        }
      }
    }
  }

  function nearestSupportedDist(s: number, marks: number[]): number {
    let best = Infinity;
    for (const m of marks) {
      let d = Math.abs(m - s);
      if (runtime.closed && L > 0) d = Math.min(d, L - d);
      best = Math.min(best, d);
    }
    return best;
  }

  // 6d. MERGE — collapse nearby ground footers onto shared pads (before bracing
  // so braces follow the updated column feet). Includes explicit mergeFeet groups.
  mergeNearbyFeet();

  // 6e. FORCE FOOT — pin base points after merge so manual XZ wins over clustering.
  applyForcedFeet();

  // 6f. Re-snap mergeFeet groups so a forceFoot on one member keeps the shared pad.
  resyncMergeFeetGroups();

  // 7. BRACE — straight (level) beams on a shared world-Y grid.
  // Cross-station: with braceMainOnly, only main↔main. Same-station main↔triangle
  // pairs are always allowed so the two legs of one piece stay laced together.
  // braceMinHeight is the lowest height above the higher foot where a brace may sit.
  const gridOffset = params.beamHeightGridOffset;
  const gridStep = Math.max(0.1, params.beamHeightGridStep);
  for (let i = 0; i < placed.length; i++) {
    for (let j = i + 1; j < placed.length; j++) {
      const a = placed[i];
      const b = placed[j];
      if (!shouldBracePair(a, b)) continue;
      const dist = a.foot.distanceTo(b.foot);
      if (dist < 1 || dist > params.maxSpan) continue;

      const baseY = Math.max(a.foot.y, b.foot.y);
      const yLo = baseY + Math.max(0, params.braceMinHeight);
      const yHi = Math.min(a.top.y, b.top.y);
      if (yHi - yLo < 1e-3) continue;

      const gridYs = braceGridHeights(yLo, yHi, gridOffset, gridStep);
      for (const y of gridYs) {
        const pA = pointAtWorldY(a, y);
        const pB = pointAtWorldY(b, y);
        if (!pA || !pB) continue;
        if (pA.distanceTo(pB) < 1e-3) continue;
        if (!bvh.intersectsCapsule(pA, pB, params.braceRadius, -1, 0)) {
          prims.push({ kind: 'brace', a: pA, b: pB, radius: params.braceRadius });
        }
      }
    }
  }

  const feet = placed.map((c) => ({
    attachS: c.attachS,
    role: c.role,
    pos: c.foot.clone(),
  }));
  return { primitives: prims, feet, skipped, warnings };

  // ── helpers ──────────────────────────────────────────────────────────────

  /** Snap placed column feet to forced plan positions (terrain Y). */
  function applyForcedFeet(): void {
    for (const o of overrides) {
      if (o.kind !== 'forceFoot') continue;
      const fs = runtime.centerline.normalizeS(o.s);
      const role = o.role ?? 'main';
      const col = findNearestPlaced(fs, role);
      if (!col) {
        warnings.push(`Forced foot at s=${fs.toFixed(0)} m — no ${role} column to move`);
        continue;
      }
      const groundY = terrain.heightAt(o.x, o.z);
      const newFoot = new Vector3(o.x, groundY, o.z);
      if (newFoot.distanceToSquared(col.foot) < 1e-12) continue;
      moveColumnFoot(col, newFoot);
    }
  }

  function findNearestPlaced(s: number, role: 'main' | 'triangle'): PlacedColumn | null {
    const idx = indexOfNearestPlaced(s, role);
    return idx >= 0 ? placed[idx] : null;
  }

  function indexOfNearestPlaced(s: number, role: 'main' | 'triangle'): number {
    const fs = runtime.centerline.normalizeS(s);
    let best = -1;
    let bestD = Infinity;
    for (let i = 0; i < placed.length; i++) {
      const c = placed[i];
      if (c.role !== role) continue;
      let d = Math.abs(c.attachS - fs);
      if (runtime.closed && L > 0) d = Math.min(d, L - d);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    // Require a reasonably local match so a stray override cannot yank a distant mast.
    const tol = Math.max(arcSpacing, params.trackSpacing, 2);
    return best >= 0 && bestD <= tol ? best : -1;
  }

  /**
   * After forceFoot may have split a shared pad, snap each mergeFeet cluster
   * back together. Prefer forced members' positions; otherwise the centroid.
   */
  function resyncMergeFeetGroups(): void {
    const n = placed.length;
    if (n < 2) return;

    const parent = Array.from({ length: n }, (_, i) => i);
    const find = (i: number): number => {
      while (parent[i] !== i) {
        parent[i] = parent[parent[i]];
        i = parent[i];
      }
      return i;
    };
    const unite = (a: number, b: number): void => {
      const ra = find(a);
      const rb = find(b);
      if (ra !== rb) parent[rb] = ra;
    };

    let anyOverride = false;
    for (const o of overrides) {
      if (o.kind !== 'mergeFeet') continue;
      const idxs: number[] = [];
      for (const m of mergeFeetMembers(o)) {
        const i = indexOfNearestPlaced(m.s, m.role);
        if (i >= 0 && !idxs.includes(i)) idxs.push(i);
      }
      if (idxs.length < 2) continue;
      anyOverride = true;
      for (let k = 1; k < idxs.length; k++) unite(idxs[0], idxs[k]);
    }
    if (!anyOverride) return;

    const clusters = new Map<number, number[]>();
    for (let i = 0; i < n; i++) {
      const r = find(i);
      const list = clusters.get(r) ?? [];
      list.push(i);
      clusters.set(r, list);
    }

    let any = false;
    for (const idxs of clusters.values()) {
      if (idxs.length < 2) continue;
      const cols = idxs.map((i) => placed[i]);
      const alreadyTogether = cols.every(
        (c) => c.foot.distanceToSquared(cols[0].foot) < 1e-12,
      );
      if (alreadyTogether) continue;

      const forced = cols.filter((c) => hasForceFoot(c.attachS, c.role));
      const src = forced.length > 0 ? forced : cols;
      let sx = 0;
      let sy = 0;
      let sz = 0;
      for (const c of src) {
        sx += c.foot.x;
        sy += c.foot.y;
        sz += c.foot.z;
      }
      const target = new Vector3(sx / src.length, sy / src.length, sz / src.length);
      if (forced.length === 0) {
        target.y = terrain.heightAt(target.x, target.z);
      }
      for (const c of cols) moveColumnFoot(c, target);
      any = true;
    }
    if (any) rebuildFooterPads();
  }

  function hasForceFoot(s: number, role: 'main' | 'triangle'): boolean {
    const fs = runtime.centerline.normalizeS(s);
    for (const o of overrides) {
      if (o.kind !== 'forceFoot') continue;
      if ((o.role ?? 'main') !== role) continue;
      if (Math.abs(runtime.centerline.normalizeS(o.s) - fs) > 1e-4) continue;
      return true;
    }
    return false;
  }

  /** Drop all footer pads and re-emit one per unique shared foot position. */
  function rebuildFooterPads(): void {
    for (let i = prims.length - 1; i >= 0; i--) {
      if (prims[i].kind === 'footer') prims.splice(i, 1);
    }
    const emitted = new Set<string>();
    for (const c of placed) {
      const f = c.foot;
      const key = `${f.x.toFixed(3)},${f.y.toFixed(3)},${f.z.toFixed(3)}`;
      if (emitted.has(key)) continue;
      emitted.add(key);
      prims.push({
        kind: 'footer',
        pos: f.clone(),
        radius: footerPadRadius(params),
        height: 0.25,
      });
    }
  }

  function moveColumnFoot(col: PlacedColumn, newFoot: Vector3): void {
    const oldFoot = col.foot.clone();
    const top = col.top.clone();
    if (newFoot.distanceToSquared(oldFoot) < 1e-12) return;

    // Hanger joints / knee tips / retargeted triangle tops sit on the old axis —
    // slide them to the same parametric height on the new foot→top segment.
    remapColumnAxisDependents(oldFoot, top, newFoot);

    col.foot.copy(newFoot);

    let bestCol: Extract<TrackSupportPrimitive, { kind: 'column' }> | null = null;
    let bestD = Infinity;
    for (const prim of prims) {
      if (prim.kind !== 'column') continue;
      if (prim.b.distanceToSquared(oldFoot) >= 1e-6) continue;
      const d = prim.a.distanceToSquared(top);
      if (d < bestD) {
        bestD = d;
        bestCol = prim;
      }
    }
    if (bestCol) {
      bestCol.b.copy(newFoot);
      bestCol.strategy = 'forced';
    }

    const othersStillAtOld = placed.some(
      (c) => c !== col && c.foot.distanceToSquared(oldFoot) < 1e-6,
    );
    if (!othersStillAtOld) {
      for (const prim of prims) {
        if (prim.kind === 'footer' && prim.pos.distanceToSquared(oldFoot) < 1e-6) {
          prim.pos.copy(newFoot);
        }
      }
    } else {
      const hasNew = prims.some(
        (p) => p.kind === 'footer' && p.pos.distanceToSquared(newFoot) < 1e-6,
      );
      if (!hasNew) {
        prims.push({
          kind: 'footer',
          pos: newFoot.clone(),
          radius: footerPadRadius(params),
          height: 0.25,
        });
      }
    }
  }

  /**
   * After a column foot moves, keep every joint / knee attach / dependent top
   * that lived on the old foot→top axis at the same fractional height on the
   * new axis. Skips the foot endpoint (t≈0) so shared pads / other columns
   * that only meet at the old foot are not dragged along.
   */
  function remapColumnAxisDependents(oldFoot: Vector3, top: Vector3, newFoot: Vector3): void {
    const tol = Math.max(params.columnRadius * 1.5, params.braceRadius * 2, 0.15);
    const map = (p: Vector3): boolean => {
      const t = columnParam(p, oldFoot, top, tol);
      if (t == null || t < 0.02) return false;
      p.copy(newFoot.clone().lerp(top, t));
      return true;
    };

    for (const prim of prims) {
      switch (prim.kind) {
        case 'joint':
          map(prim.pos);
          break;
        case 'footer':
          break;
        case 'column':
          map(prim.a);
          break;
        case 'brace':
        case 'knee':
        case 'stub':
          map(prim.a);
          map(prim.b);
          break;
        default: {
          const _exhaustive: never = prim;
          void _exhaustive;
        }
      }
    }
    for (const c of placed) {
      map(c.top);
    }
  }

  /** Parametric t∈[0,1] when `p` lies within `tol` of segment foot→top. */
  function columnParam(p: Vector3, foot: Vector3, top: Vector3, tol: number): number | null {
    const ab = top.clone().sub(foot);
    const lenSq = ab.lengthSq();
    if (lenSq < 1e-12) return null;
    const t = p.clone().sub(foot).dot(ab) / lenSq;
    if (t < -0.02 || t > 1.02) return null;
    const tc = Math.min(1, Math.max(0, t));
    const closest = foot.clone().addScaledVector(ab, tc);
    if (closest.distanceTo(p) > tol) return null;
    return tc;
  }

  function shouldBracePair(a: PlacedColumn, b: PlacedColumn): boolean {
    const sameStation = Math.abs(a.attachS - b.attachS) < 1e-6;
    if (sameStation) {
      // Lace the main + triangle mate of one support piece together
      return a.role !== b.role;
    }
    if (params.braceMainOnly) {
      return a.role === 'main' && b.role === 'main';
    }
    return true;
  }

  /** Cluster feet within footerMergeDistance and snap columns onto shared pads. */
  function mergeNearbyFeet(): void {
    const mergeDist = params.footerMergeDistance;
    const forcedMerges = overrides.filter((o) => o.kind === 'mergeFeet');
    if ((mergeDist <= 1e-6 && forcedMerges.length === 0) || placed.length < 2) return;

    const n = placed.length;
    const parent = Array.from({ length: n }, (_, i) => i);
    const find = (i: number): number => {
      while (parent[i] !== i) {
        parent[i] = parent[parent[i]];
        i = parent[i];
      }
      return i;
    };
    const unite = (a: number, b: number): void => {
      const ra = find(a);
      const rb = find(b);
      if (ra !== rb) parent[rb] = ra;
    };

    if (mergeDist > 1e-6) {
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          if (!params.allowTriangleMerge) {
            const ri = placed[i].role;
            const rj = placed[j].role;
            if (ri !== rj && (ri === 'triangle' || rj === 'triangle')) continue;
          }
          if (horizDistXZ(placed[i].foot, placed[j].foot) <= mergeDist) unite(i, j);
        }
      }
    }

    // Explicit mergeFeet overrides — ignore distance and allowTriangleMerge.
    for (const o of forcedMerges) {
      if (o.kind !== 'mergeFeet') continue;
      const idxs: number[] = [];
      for (const m of mergeFeetMembers(o)) {
        const i = indexOfNearestPlaced(m.s, m.role);
        if (i >= 0 && !idxs.includes(i)) idxs.push(i);
      }
      if (idxs.length < 2) {
        warnings.push(
          `Merge feet @ s=${mergeFeetMembers(o)
            .map((m) => m.s.toFixed(0))
            .join('/')} — missing column(s)`,
        );
        continue;
      }
      for (let k = 1; k < idxs.length; k++) unite(idxs[0], idxs[k]);
    }

    const clusters = new Map<number, number[]>();
    for (let i = 0; i < n; i++) {
      const r = find(i);
      const list = clusters.get(r) ?? [];
      list.push(i);
      clusters.set(r, list);
    }

    const oldFeet = placed.map((p) => p.foot.clone());
    const newFeet = oldFeet.map((f) => f.clone());
    let anyMerged = false;
    for (const idxs of clusters.values()) {
      if (idxs.length < 2) continue;
      anyMerged = true;
      let sx = 0;
      let sy = 0;
      let sz = 0;
      for (const i of idxs) {
        sx += oldFeet[i].x;
        sy += oldFeet[i].y;
        sz += oldFeet[i].z;
      }
      const merged = new Vector3(sx / idxs.length, sy / idxs.length, sz / idxs.length);
      for (const i of idxs) newFeet[i].copy(merged);
    }
    if (!anyMerged) return;

    // Remap hanger joints / knee tips / retargeted tops onto each new axis
    // before swapping feet (uses pre-merge foot positions).
    for (let i = 0; i < n; i++) {
      if (oldFeet[i].distanceToSquared(newFeet[i]) < 1e-12) continue;
      remapColumnAxisDependents(oldFeet[i], placed[i].top, newFeet[i]);
    }

    for (let i = 0; i < n; i++) placed[i].foot.copy(newFeet[i]);

    for (const prim of prims) {
      if (prim.kind !== 'column') continue;
      for (let i = 0; i < n; i++) {
        if (prim.b.distanceToSquared(oldFeet[i]) < 1e-6) {
          prim.b.copy(newFeet[i]);
          break;
        }
      }
    }

    rebuildFooterPads();
  }

  function pointAtWorldY(c: PlacedColumn, worldY: number): Vector3 | null {
    const dy = c.top.y - c.foot.y;
    if (Math.abs(dy) < 1e-9) return null;
    const t = (worldY - c.foot.y) / dy;
    if (t < -1e-6 || t > 1 + 1e-6) return null;
    return c.foot.clone().lerp(c.top, Math.min(1, Math.max(0, t)));
  }

  function placeAt(cs: number, forced: boolean): boolean {
    const f = runtime.frameAt(cs);
    // 2. ANCHOR — just below the spine, along local −up, then optional stub
    //    further along −up so the column bends at an elbow rather than
    //    meeting the track in a single straight tube.
    const trackAttach = f.railPos.clone().addScaledVector(f.up, -params.attachDepth);
    const elbow =
      params.attachStub > 1e-6
        ? trackAttach.clone().addScaledVector(f.up, -params.attachStub)
        : trackAttach.clone();
    const groundY = terrain.heightAt(elbow.x, elbow.z);
    if (elbow.y - groundY < params.minTrackHeight) return true; // track basically on the ground

    const excludeRange = attachExcludeRange(params, arcSpacing);

    // 3–4. CAST + REJECT: vertical first, then a fan of leans (5. RECOVER i).
    // A triangle mate is best-effort: if it cannot clear, keep the primary alone.
    const maxLean = forced ? params.maxLeanDeg * 1.6 : params.maxLeanDeg;
    const directions = leanFan(f, maxLean);
    for (const { dir, strategy } of directions) {
      const foot = castToGround(elbow, dir, groundY);
      if (!foot) continue;
      if (bvh.intersectsCapsule(elbow, foot, params.columnRadius, cs, excludeRange)) continue;
      if (hitsPlacedColumn(elbow, foot)) continue;

      const mateFoot = tryTriangleMate(elbow, foot, f, cs, excludeRange, groundY);
      emitAttachedColumn(trackAttach, elbow, foot, strategy, cs);
      if (mateFoot) emitLeg(elbow, mateFoot, 'aframe', cs, 'triangle');
      return true;
    }

    // 5.ii RECOVER — A-frame: stub to elbow, then two legs splayed sideways
    const spread = Math.max(2.2, params.triangleSpread || 2.2);
    const side = horizontalRight(f);
    const aframePairs: [Vector3, Vector3][] = [
      [side.clone().multiplyScalar(spread), side.clone().multiplyScalar(-spread)],
      // One side blocked by clearance: both legs on the clear side at different spreads
      [side.clone().multiplyScalar(spread), side.clone().multiplyScalar(spread * 1.7)],
      [side.clone().multiplyScalar(-spread), side.clone().multiplyScalar(-spread * 1.7)],
    ];
    let singleAframe: Vector3 | null = null;
    for (const [offA, offB] of aframePairs) {
      const legA = new Vector3(elbow.x + offA.x, groundY, elbow.z + offA.z);
      const legB = new Vector3(elbow.x + offB.x, groundY, elbow.z + offB.z);
      if (horizDistXZ(legA, legB) < params.columnRadius * 4) continue;
      const okA =
        !bvh.intersectsCapsule(elbow, legA, params.columnRadius, cs, excludeRange) &&
        !hitsPlacedColumn(elbow, legA);
      const okB =
        !bvh.intersectsCapsule(elbow, legB, params.columnRadius, cs, excludeRange) &&
        !hitsPlacedColumn(elbow, legB);
      if (okA && okB) {
        emitStub(trackAttach, elbow, cs);
        emitLeg(elbow, legA, 'aframe', cs, 'main');
        emitLeg(elbow, legB, 'aframe', cs, 'triangle');
        return true;
      }
      // Remember a single clear leg; prefer dual pairs above before accepting it.
      if (!singleAframe) {
        if (okA) singleAframe = legA;
        else if (okB) singleAframe = legB;
      }
    }
    if (singleAframe) {
      emitStub(trackAttach, elbow, cs);
      emitLeg(elbow, singleAframe, 'aframe', cs, 'main');
      return true;
    }

    // 5.iii RECOVER — knee brace to the nearest clear existing column
    if (tryKneeToExisting(trackAttach, elbow, cs, forced ? 12 : 8, excludeRange)) return true;

    // 5.iv — skip; the neighbours carry the span
    return false;
  }

  /**
   * Fill a trackSpacing gap: hang to a nearby main column, unless a ground
   * column would be shorter — then place a normal ground support instead.
   */
  function placeTrackSpacing(cs: number): boolean {
    const f = runtime.frameAt(cs);
    const trackAttach = f.railPos.clone().addScaledVector(f.up, -params.attachDepth);
    const elbow =
      params.attachStub > 1e-6
        ? trackAttach.clone().addScaledVector(f.up, -params.attachStub)
        : trackAttach.clone();
    const groundY = terrain.heightAt(elbow.x, elbow.z);
    const groundLen = elbow.y - groundY;
    if (groundLen < params.minTrackHeight) return true;

    const excludeRange = attachExcludeRange(params, arcSpacing);
    const maxReach = Math.max(params.trackSpacing * 1.5, params.maxSpan, 12);
    const knee = findBestKnee(elbow, cs, maxReach, excludeRange);
    const preferGround = !knee || groundLen < knee.dist;

    if (preferGround) {
      if (placeAt(cs, true)) return true;
      if (knee) {
        emitKnee(trackAttach, elbow, knee.point, cs);
        return true;
      }
      return false;
    }

    emitKnee(trackAttach, elbow, knee.point, cs);
    return true;
  }

  function findBestKnee(
    elbow: Vector3,
    cs: number,
    maxKnee: number,
    excludeRange: number,
  ): { point: Vector3; dist: number } | null {
    let best: { point: Vector3; dist: number } | null = null;
    // Attach hangers to main masts so the joint sphere can also take the triangle mate.
    const mains = placed.filter((c) => c.role === 'main');
    const candidates = mains.length > 0 ? mains : placed;
    for (const col of candidates) {
      // Prefer a mid/upper attachment on the existing column
      for (const t of [0.55, 0.7, 0.85]) {
        const mid = col.foot.clone().lerp(col.top, t);
        const d = mid.distanceTo(elbow);
        if (d > maxKnee) continue;
        if (bvh.intersectsCapsule(elbow, mid, params.braceRadius, cs, excludeRange)) continue;
        if (!best || d < best.dist) best = { point: mid, dist: d };
      }
    }
    return best;
  }

  function emitKnee(trackAttach: Vector3, elbow: Vector3, point: Vector3, attachS: number): void {
    emitStub(trackAttach, elbow, attachS);
    prims.push({ kind: 'knee', a: elbow.clone(), b: point.clone(), radius: params.braceRadius * 1.4 });
    // Joint at the column connection so the angled meet is solid for printing
    prims.push({
      kind: 'joint',
      pos: point.clone(),
      radius: Math.max(params.columnRadius, params.braceRadius * 1.4),
    });
  }

  /**
   * Move triangle mate tops from the track elbow down onto any track-spacing
   * hanger joint that sits on the same station's main column.
   */
  function retargetTriangleTopsToHangerJoints(): void {
    const excludeRange = attachExcludeRange(params, arcSpacing);
    const jointTol = Math.max(params.columnRadius * 1.5, params.braceRadius * 2);

    for (const tri of placed) {
      if (tri.role !== 'triangle') continue;
      const main = placed.find(
        (c) => c.role === 'main' && Math.abs(c.attachS - tri.attachS) < 1e-6,
      );
      if (!main) continue;

      const targets: Vector3[] = [];
      for (const prim of prims) {
        if (prim.kind !== 'knee') continue;
        if (!pointOnColumnInterior(prim.b, main, jointTol)) continue;
        targets.push(prim.b.clone());
      }
      if (targets.length === 0) continue;

      // Highest hanger joint (closest to the former top sphere).
      targets.sort((a, b) => b.y - a.y);
      const target = targets[0];
      if (target.distanceToSquared(tri.top) < 1e-6) continue;
      if (bvh.intersectsCapsule(target, tri.foot, params.columnRadius, tri.attachS, excludeRange)) {
        continue;
      }
      if (hitsPlacedColumn(target, tri.foot, tri.attachS)) continue;

      const oldTop = tri.top.clone();
      for (const prim of prims) {
        if (prim.kind !== 'column') continue;
        if (prim.a.distanceToSquared(oldTop) > 1e-8) continue;
        if (prim.b.distanceToSquared(tri.foot) > 1e-8) continue;
        prim.a.copy(target);
        break;
      }
      tri.top.copy(target);
    }
  }

  /** True when `p` lies near the open segment of a column (not the elbow/foot ends). */
  function pointOnColumnInterior(p: Vector3, col: PlacedColumn, tol: number): boolean {
    const ab = col.top.clone().sub(col.foot);
    const lenSq = ab.lengthSq();
    if (lenSq < 1e-12) return false;
    const t = p.clone().sub(col.foot).dot(ab) / lenSq;
    if (t < 0.05 || t > 0.98) return false;
    const closest = col.foot.clone().addScaledVector(ab, t);
    return closest.distanceTo(p) <= tol;
  }

  function tryKneeToExisting(
    trackAttach: Vector3,
    elbow: Vector3,
    cs: number,
    maxKnee: number,
    excludeRange: number,
  ): boolean {
    const best = findBestKnee(elbow, cs, maxKnee, excludeRange);
    if (!best) return false;
    emitKnee(trackAttach, elbow, best.point, cs);
    return true;
  }

  /**
   * Find a 2nd ground leg forming a triangle with the primary.
   * Best-effort: returns null when no clear mate exists (caller keeps the primary).
   * Tries opposite-lean first, then ±lateral / ±tangent and slightly wider spreads —
   * opposite often fails clearance for the same reason the primary had to lean.
   */
  function tryTriangleMate(
    elbow: Vector3,
    primaryFoot: Vector3,
    f: { right: Vector3; tan: Vector3 },
    cs: number,
    excludeRange: number,
    groundY: number,
  ): Vector3 | null {
    if (params.triangleSpread <= 1e-6) return null;

    const primaryDir = primaryFoot.clone().sub(elbow);
    const height = Math.max(1e-6, elbow.y - groundY);
    const leanHoriz = new Vector3(primaryDir.x, 0, primaryDir.z);
    const leanDeg = (Math.atan2(leanHoriz.length(), Math.max(1e-6, -primaryDir.y)) * 180) / Math.PI;
    const tallEnough = height >= params.triangleMinHeight;
    // Pure vertical never triggers via the lean gate (use triangleMinHeight for that).
    const leanGate = Math.max(0.5, params.triangleMinLeanDeg);
    const leanedEnough = leanDeg >= leanGate;
    if (!tallEnough && !leanedEnough) return null;

    const candidates = triangleMateDirs(leanHoriz, f);
    const spreads = [params.triangleSpread, params.triangleSpread * 1.5, params.triangleSpread * 2];

    for (const spread of spreads) {
      if (spread <= 1e-6) continue;
      for (const outward of candidates) {
        const secondFoot = new Vector3(elbow.x, groundY, elbow.z).addScaledVector(outward, spread);
        if (horizDistXZ(primaryFoot, secondFoot) < params.columnRadius * 4) continue;
        if (bvh.intersectsCapsule(elbow, secondFoot, params.columnRadius, cs, excludeRange)) continue;
        if (hitsPlacedColumn(elbow, secondFoot, cs)) continue;
        return secondFoot;
      }
    }
    return null;
  }

  /** Mate search order: opposite lean (classic), then plan ±right / ±tan. */
  function triangleMateDirs(leanHoriz: Vector3, f: { right: Vector3; tan: Vector3 }): Vector3[] {
    const dirs: Vector3[] = [];
    const pushUnique = (v: Vector3) => {
      if (v.lengthSq() < 1e-8) return;
      const n = v.clone().normalize();
      if (dirs.some((d) => d.dot(n) > 0.98)) return;
      dirs.push(n);
    };

    if (leanHoriz.lengthSq() > 1e-8) {
      pushUnique(leanHoriz.clone().negate());
      // Same-side further out can still brace when the obstacle blocks the opposite side
      pushUnique(leanHoriz);
    }
    const hr = horizontalRight(f);
    pushUnique(hr);
    pushUnique(hr.clone().negate());
    const ht = new Vector3(f.tan.x, 0, f.tan.z);
    if (ht.lengthSq() > 1e-6) {
      pushUnique(ht);
      pushUnique(ht.clone().negate());
    }
    return dirs;
  }

  function leanFan(f: { up: Vector3; right: Vector3; tan: Vector3 }, maxLeanDeg: number): {
    dir: Vector3;
    strategy: PlacementStrategy;
  }[] {
    const out: { dir: Vector3; strategy: PlacementStrategy }[] = [];
    const down = new Vector3(0, -1, 0);
    out.push({ dir: down.clone(), strategy: 'vertical' });
    const lean = (maxLeanDeg * Math.PI) / 180;
    const horizR = horizontalRight(f);
    const horizT = new Vector3(f.tan.x, 0, f.tan.z);
    if (horizT.lengthSq() > 1e-6) horizT.normalize();
    else horizT.set(1, 0, 0);
    for (const frac of [0.5, 1]) {
      for (const side of [horizR, horizR.clone().negate(), horizT, horizT.clone().negate()]) {
        const dir = down
          .clone()
          .multiplyScalar(Math.cos(lean * frac))
          .addScaledVector(side, Math.sin(lean * frac))
          .normalize();
        out.push({ dir, strategy: 'leaning' });
      }
    }
    return out;
  }

  function horizontalRight(f: { right: Vector3; tan: Vector3 }): Vector3 {
    const r = new Vector3(f.right.x, 0, f.right.z);
    if (r.lengthSq() < 1e-6) {
      r.set(-f.tan.z, 0, f.tan.x);
    }
    return r.normalize();
  }

  function castToGround(from: Vector3, dir: Vector3, groundY: number): Vector3 | null {
    if (dir.y >= -0.2) return null; // must go meaningfully downward
    const t = (groundY - from.y) / dir.y;
    if (t <= 0 || t > 200) return null;
    return from.clone().addScaledVector(dir, t);
  }

  function hitsPlacedColumn(a: Vector3, b: Vector3, ignoreAttachS?: number): boolean {
    for (const col of placed) {
      if (ignoreAttachS != null && Math.abs(col.attachS - ignoreAttachS) < 1e-6) continue;
      const d = segDist(a, b, col.top, col.foot);
      if (d < params.columnRadius * 2.2) return true;
    }
    return false;
  }

  function emitStub(trackAttach: Vector3, elbow: Vector3, attachS: number): void {
    if (trackAttach.distanceToSquared(elbow) < 1e-8) return;
    prims.push({
      kind: 'stub',
      a: trackAttach.clone(),
      b: elbow.clone(),
      radiusA: params.attachStubRadius,
      radiusB: params.columnRadius,
      attachS,
    });
    // Ball joint at the elbow — overlaps stub tip and column head so an angled
    // bend has no void (needed for later boolean-union / 3D print).
    // Sphere diameter matches the thicker tube at the elbow (stub tip / column).
    const jointR = Math.max(params.columnRadius, params.attachStubRadius);
    prims.push({ kind: 'joint', pos: elbow.clone(), radius: jointR });
  }

  /** Stub from track + one leg from elbow to foot. */
  function emitAttachedColumn(
    trackAttach: Vector3,
    elbow: Vector3,
    foot: Vector3,
    strategy: PlacementStrategy,
    attachS: number,
  ): void {
    emitStub(trackAttach, elbow, attachS);
    emitLeg(elbow, foot, strategy, attachS, 'main');
  }

  function emitLeg(
    top: Vector3,
    foot: Vector3,
    strategy: PlacementStrategy,
    attachS: number,
    role: 'main' | 'triangle' = 'main',
  ): void {
    prims.push({ kind: 'column', a: top.clone(), b: foot.clone(), radius: params.columnRadius, strategy });
    prims.push({ kind: 'footer', pos: foot.clone(), radius: footerPadRadius(params), height: 0.25 });
    placed.push({ attachS, top: top.clone(), foot: foot.clone(), role });
  }
}

/** Uniform ground-pad half-side — same for every footer, shared or not. */
function footerPadRadius(params: SupportParams): number {
  return params.columnRadius * 2.2;
}
